import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '@/hooks/api/queryKeys';
import { axiosInstance as axios } from '@/utils/axiosConfig';

interface UpdateTableParams {
  object_key: string;
  name: string;
  identifier: string;
}

interface CopyTableParams {
  object_key: string;
  fields: string[];
  object_name: string;
}

interface CopyFieldsParams extends CopyTableParams {
  target_object_id: string;
}

async function copyTable({ object_key, object_name, fields }: CopyTableParams) {
  const data = { object_name, fields };

  const { data: responseData } = await axios.post(`/v1/objects/${object_key}/copy`, data, {
    withCredentials: true
  });
  return responseData;
}

async function copyFields({ object_key, object_name, fields, target_object_id }: CopyFieldsParams) {
  const data = { object_name, fields, target_object_id };

  const { data: responseData } = await axios.post(`/v1/objects/${object_key}/copy_fields`, data, {
    withCredentials: true
  });
  return responseData;
}

async function sortTables(order: string[]) {
  const { data } = await axios.post(
    '/v1/objects/sort',
    { order },
    {
      withCredentials: true
    }
  );
  return data;
}

async function updateTable({ object_key, name, identifier }: UpdateTableParams) {
  const data = { name, identifier };

  const { data: responseData } = await axios.put(`/v1/objects/${object_key}`, data, {
    withCredentials: true
  });

  return responseData;
}

async function deleteTable(object_key: string) {
  await axios.delete(`/v1/objects/${object_key}`, {
    withCredentials: true
  });
}

export function useTableMutation() {
  const queryClient = useQueryClient();

  return {
    sortMutation: useMutation({
      mutationFn: (order: string[]) => sortTables(order),
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: [queryKeys.application] });
      }
    }),
    updateMutation: useMutation({
      mutationFn: ({ object_key, name, identifier }: UpdateTableParams) =>
        updateTable({ object_key, name, identifier }),
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: [queryKeys.application] });
      }
    }),
    deleteMutation: useMutation({
      mutationFn: (object_key: string) => deleteTable(object_key),
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: [queryKeys.application] });
      }
    }),
    copyTableMutation: useMutation({
      mutationFn: (params: CopyTableParams) => copyTable(params),
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: [queryKeys.application] });
      }
    }),
    copyFieldsMutation: useMutation({
      mutationFn: (params: CopyFieldsParams) => copyFields(params),
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: [queryKeys.application] });
      }
    })
  };
}
